import { Component, OnInit, inject, signal, Input } from '@angular/core';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { Column } from '@sharedComponents/table-dynamic/interfaces';
import { ConfiguracionesTecnicasService } from '@servicios/configuracionesTecnicas.service';
import { TechConfigModels } from '@interfaces/operation-result-types';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { ConfigService } from '@servicios/config.service';

@Component({
  selector: 'app-concentration-cell',
  standalone: true,
  imports: [CommonModule, TooltipModule, ButtonModule],
  template: `
    <div class="flex align-items-center gap-2">
      <span>{{ getConcentracionText() }}</span>
      <p-button 
          *ngIf="data?.composition?.length > 1" 
          icon="pi pi-info-circle" 
          [text]="true" 
          [rounded]="true" 
          size="small" 
          severity="info" 
          (onClick)="params.onClick(data)"
          pTooltip="Ver composición completa" 
          tooltipPosition="top">
      </p-button>
    </div>
  `
})
export class ConcentrationCellComponent {
  @Input() data: any;
  @Input() params: any;

  getConcentracionText(): string {
    if (this.data?.composition && Array.isArray(this.data.composition) && this.data.composition.length > 0) {
      const first = this.data.composition[0];
      const unit = this.params?.units?.().find((u: any) => u.value === first.unit_id);
      return `${first.concentration || ''} ${unit ? unit.label : (first.unit_id || '')}`.trim();
    }
    return '-';
  }
}

@Component({
  selector: 'app-table-test-items-summary',
  standalone: false,
  templateUrl: './table-test-items-summary.component.html',
  styleUrls: ['./table-test-items-summary.component.scss']
})
export class TableTestItemsSummaryComponent implements OnInit {
  private service = inject(InventoryService);
  
  loading = signal<boolean>(false);
  data = signal<any[]>([]);
  totalRecords = signal<number>(0);
  first = signal<number>(0);
  pageSize = signal<number>(10);
  cols = signal<Column[]>([]);
  units = signal<any[]>([]);

  private techConfigService = inject(ConfiguracionesTecnicasService);
  private router = inject(Router);
  private configService = inject(ConfigService);

  get massBalanceActive(): boolean {
    return !!this.configService.appSettings?.frontend?.modules?.laboratory?.samples?.massBalance?.active;
  }

  get actionButtons() {
    const buttons = [
      {
        icon: 'pi pi-pencil',
        label: 'Editar',
        type: 'edit',
        action: (item: any) => this.handleEdit(item)
      }
    ];

    if (this.massBalanceActive) {
      buttons.push({
        icon: 'pi pi-chart-pie',
        label: 'Balance de Masas',
        type: 'custom',
        action: (item: any) => this.viewMassBalance(item)
      });
    }

    return buttons;
  }

  viewMassBalance(item: any) {
    if (item?.id) {
      this.router.navigateByUrl(`/laboratory/mass-balance/${item.id}`).then(success => {
        if (!success) console.error('Navigation failed to /laboratory/mass-balance');
      });
    }
  }

  // state for popup
  selectedItemComposition = signal<any[]>([]);
  showCompositionDialog = signal(false);

  async ngOnInit() {
    this.setupColumns();
    await this.loadUnits();
    this.loadData();
  }

  async loadUnits() {
    try {
      const unitsRes = await firstValueFrom(this.techConfigService.getAll(TechConfigModels.units).valueChanges);
      if (unitsRes?.data?.techConfigs) {
        const mappedUnits = unitsRes.data.techConfigs.map((u: any) => ({
          label: u.translation?.['es'] || u.value || u.abbreviation,
          value: u.id
        }));
        this.units.set(mappedUnits);
      }
    } catch (e) {
      console.error('Error loading units', e);
    }
  }

  setupColumns() {
    this.cols.set([
      { 
        field: 'estado', 
        header: 'Estado', 
        visible: true, 
        sortable: true, 
        filter: { 
          enable: true, 
          type: 'select',
          options: [{label: 'ALTA', value: 'ALTA'}, {label: 'BAJA', value: 'BAJA'}],
          optionLabel: 'label',
          optionValue: 'value',
          showMenu: false
        }, 
        type: 'tag', 
        valueGetter: (item) => this.getEstado(item), 
        componentParams: { severityGetter: (item: any) => this.getEstado(item) === 'ALTA' ? 'success' : 'danger' } 
      },
      { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains' } },
      { 
        field: 'concentracion', 
        header: 'Concentración', 
        visible: true, 
        sortable: false,
        filter: { enable: false, type: 'text' }, 
        component: ConcentrationCellComponent, 
        componentParams: { 
          units: this.units, 
          onClick: (item: any) => this.openCompositionDialog(item) 
        } 
      },
      { field: 'internal_code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains' } },
      { field: 'lot_number', header: 'Lote', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains' } },
      { field: 'study.code', header: 'Estudio', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains' } },
      { field: 'brand.name', header: 'Fabricante', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains' } },
      { field: 'form.name', header: 'Presentación', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains' } },
      { field: 'expiration_date', header: 'F. Vencimiento', visible: true, filter: { enable: false, type: 'text' }, pipe: 'date' }
    ]);
  }

  async loadData(event?: any) {
    this.loading.set(true);
    try {
      const first = event?.first !== undefined ? event.first : this.first();
      const rows = event?.rows !== undefined ? event.rows : this.pageSize();
      const sortField = event?.sortField || undefined;
      const sortOrder = event?.sortOrder !== undefined ? event.sortOrder : undefined;
      const search = event?.globalFilter || event?.filters?.global?.value || '';

      this.first.set(first);
      this.pageSize.set(rows);

      const res = await this.service.getAll('items', {
        page: Math.floor(first / rows) + 1 || 1,
        limit: rows,
        search: search,
        sortBy: sortField,
        sortOrder: sortOrder ? (sortOrder > 0 ? 'asc' : 'desc') : undefined,
        itemType: 'TEST_ITEM',
        filters: event?.filters || undefined
      });

      this.data.set(res.data);
      this.totalRecords.set(res.total);
    } catch (e) {
      console.error(e);
    } finally {
      this.loading.set(false);
    }
  }

  getEstado(item: any): 'ALTA' | 'BAJA' {
    if (!item.expiration_date) return 'ALTA';
    return new Date(item.expiration_date) < new Date() ? 'BAJA' : 'ALTA';
  }

  getUnitLabel(unitId: string): string {
    const unit = this.units().find(u => u.value === unitId);
    return unit ? unit.label : (unitId || '-');
  }

  handleEdit(item: any) {
    if (item && item.id) {
      this.router.navigate(['/inventory/items', item.id]);
    }
  }

  handleNew() {
    this.router.navigate(['/inventory/items/new'], { queryParams: { type: 'TEST_ITEM' } });
  }

  handleTableAction(event: { item: any, col: Column }) {
    if (event.col.field === 'compo_btn') {
      this.openCompositionDialog(event.item);
    }
  }

  openCompositionDialog(item: any) {
     this.selectedItemComposition.set(item.composition || []);
     this.showCompositionDialog.set(true);
  }
}
